import { Trans, useLingui } from '@lingui/react'
import type { MetadataCustomData } from '@redteclab/api/clients/adserver-api'
import classNames from 'clsx'
import React, { useState } from 'react'

import { AD_SERVER_METADATA_DATA_ATTR } from 'shared/consts/adServer'
import { getMetadataContentFromPlacement } from 'shared/utils/adServerUtils'
import { isMobile, isTablet } from 'shared/utils/deviceClassUtils'
import { isEmpty } from 'shared/utils/objectUtils'
import type {
  AdServerDecisionResponseEvent,
  AdSlotDimensions,
  Placement,
} from 'types/adServerTypes'
import {
  AdServerDecisionResponseContentType,
  AdServerPlacementTemplate,
} from 'types/adServerTypes'
import { CLIENTSIDE_HOOK_DATA_ATTR } from 'views/assets/scripts/consts'
import IconCross from 'views/components/atoms/Icon/IconCross'
import Image from 'views/components/atoms/Image'
import TextWithIcon from 'views/components/molecules/TextWithIcon'
import {
  AD_BANNER_AUTO_LOAD_DATA_ATTR,
  AD_BANNER_AUTO_REFRESH_DATA_ATTR,
  AD_BANNER_AUTO_REFRESH_IDS,
  AD_BANNER_CLIENTSIDE_HOOK,
  AD_BANNER_CLOSE_CLIENTSIDE_HOOK,
  AD_BANNER_CONTAINER_DATA_ATTR,
  AD_BANNER_EXCLUDE_PAGE_SPECIFIC_CONTEXT_ATTR,
  AD_BANNER_HEIGHT_DATA_ATTR,
  AD_BANNER_LEGAL_BUTTON_CLIENTSIDE_HOOK,
  AD_BANNER_STATUS_DATA_ATTR,
  AD_BANNER_TAGS_DATA_ATTR,
  AD_BANNER_WIDTH_DATA_ATTR,
} from 'views/consts/adBanner'
import { SIZES_BY_ID } from 'views/consts/adSlots'
import { usePublicConfig } from 'views/utils/shopAndRuntimeConfigUtils'

import './AdBanner.scss'

interface AdBannerProps {
  id?: string
  placement?: Placement
  events?: Array<AdServerDecisionResponseEvent>
  dimensions?: AdSlotDimensions
  className?: string
  optional?: boolean
  isAutoLoaded?: boolean
  renderBannerOnServerSide?: boolean
  bannerTags?: Array<string>
  excludePageSpecificContext?: boolean
  customData?: MetadataCustomData
  roundedCorners?: boolean
}

const AdBanner = React.forwardRef<HTMLDivElement, AdBannerProps>(
  (props, ref) => {
    const { i18n } = useLingui()
    const { deviceClass } = usePublicConfig()
    const {
      id,
      placement,
      placement: {
        body,
        width,
        height,
        template,
        imageUrl,
        clickUrl,
        title,
        type,
        customData,
      } = {},
      dimensions,
      className,
      optional,
      isAutoLoaded,
      renderBannerOnServerSide,
      bannerTags,
      excludePageSpecificContext,
      roundedCorners = true,
    } = props

    const [legalTextVisibility, setlegalTextVisibility] = useState(false)

    if (!id && !dimensions) {
      throw new Error('Neither id nor EAN nor dimensions is defined')
    }

    const classes = classNames(
      'as-placeholder',
      {
        'as-placeholder--optional': optional,
      },
      className
    )

    let slotWidth
    let slotHeight

    if (dimensions) {
      slotWidth = dimensions.width
      slotHeight = dimensions.height
    } else {
      const sizes = SIZES_BY_ID[id!]

      if (!sizes) {
        return null
      }

      const { width: widthById, height: heightById } =
        sizes[
          isMobile(deviceClass) || isTablet(deviceClass) ? 'small' : 'large'
        ] || {}

      slotWidth = widthById
      slotHeight = heightById
    }

    if (!slotWidth || !slotHeight) {
      return null
    }

    const renderRawTypeBannerOnServerSide =
      renderBannerOnServerSide &&
      type === AdServerDecisionResponseContentType.raw

    const renderImageTypeBannerOnServerSide =
      renderBannerOnServerSide &&
      template === AdServerPlacementTemplate.image &&
      type === AdServerDecisionResponseContentType.html &&
      imageUrl &&
      clickUrl

    const renderBannerOnClientSide = !renderBannerOnServerSide
    const includeAdServerMetadataAttr =
      !isEmpty(placement) &&
      (renderImageTypeBannerOnServerSide || renderRawTypeBannerOnServerSide)
    const bannerTagsWithId = id ? [...(bannerTags || []), id] : bannerTags
    const paddingTop = `${100 / (slotWidth / slotHeight)}%`
    const legalLabel: string | undefined = customData?.legalButton?.label
    const legalText: string | undefined = customData?.legalButton?.text
    const autoRefresh = id ? AD_BANNER_AUTO_REFRESH_IDS.includes(id) : false

    return (
      <div
        className={classNames(
          'l-flex l-flex--center l-flex--stretch-children m-AdBanner__container',
          roundedCorners && 'm-AdBanner__container--rounded'
        )}
        aria-label={title}
      >
        <div
          {...{
            ref,
            className: classes,
            style: {
              paddingTop,
            },
            [AD_BANNER_EXCLUDE_PAGE_SPECIFIC_CONTEXT_ATTR]:
              excludePageSpecificContext ? 'true' : undefined,
            [AD_BANNER_AUTO_LOAD_DATA_ATTR]: isAutoLoaded ? 'true' : undefined,
            [AD_BANNER_STATUS_DATA_ATTR]: renderBannerOnClientSide
              ? ''
              : 'ready',
            [AD_BANNER_TAGS_DATA_ATTR]: bannerTagsWithId?.toString(),
            [AD_BANNER_WIDTH_DATA_ATTR]: slotWidth,
            [AD_BANNER_HEIGHT_DATA_ATTR]: slotHeight,
            [CLIENTSIDE_HOOK_DATA_ATTR]: renderBannerOnClientSide
              ? AD_BANNER_CLIENTSIDE_HOOK
              : undefined,
            [AD_SERVER_METADATA_DATA_ATTR]: includeAdServerMetadataAttr
              ? JSON.stringify(getMetadataContentFromPlacement(placement))
              : undefined,
            [AD_BANNER_AUTO_REFRESH_DATA_ATTR]: autoRefresh
              ? 'true'
              : undefined,
          }}
        >
          <div
            {...{
              [AD_BANNER_CONTAINER_DATA_ATTR]: '',
            }}
          >
            {/* Render Image|HTML Type AdBanner on Server Side*/}
            {renderImageTypeBannerOnServerSide && (
              <div style={{ width, height }}>
                <a
                  href={clickUrl}
                  rel="nofollow noopener"
                  aria-label={i18n._('adBanner.title')}
                >
                  <Image src={imageUrl} alt={title} />
                </a>
              </div>
            )}

            {/* Render Raw Type AdBanner on Server Side*/}
            {renderRawTypeBannerOnServerSide && (
              <div style={{ width, height }}>
                <iframe
                  title={title || i18n._('adBanner.title')}
                  sandbox="allow-top-navigation-by-user-activation allow-scripts allow-popups"
                  width={width}
                  height={height}
                  frameBorder={0}
                  scrolling="no"
                  src={`data:text/html,${encodeURIComponent(body!)}`}
                />
              </div>
            )}
          </div>
          <button
            {...{
              className: 'as-placeholder__close',
              [CLIENTSIDE_HOOK_DATA_ATTR]: AD_BANNER_CLOSE_CLIENTSIDE_HOOK,
              type: 'button',
              hidden: true,
            }}
          >
            <TextWithIcon
              icon={<IconCross className="as-placeholder__closeIcon" />}
            >
              <span className="u-visuallyhidden">
                <Trans id="general.link.label.close" />
              </span>
            </TextWithIcon>
          </button>
          {renderBannerOnServerSide && legalLabel && legalText ? (
            <button
              className="as-placeholder__legalButton"
              type="button"
              onClick={() => {
                setlegalTextVisibility(!legalTextVisibility)
              }}
            >
              <span className="as-placeholder__legalButton-label">
                {legalLabel}
              </span>
              <span
                className="as-placeholder__legalButton-text"
                style={{ height: legalTextVisibility ? 'auto' : 0 }}
              >
                <span
                  className="as-placeholder__legalButton-text--inner"
                  dangerouslySetInnerHTML={{ __html: legalText }}
                />
              </span>
            </button>
          ) : (
            <button
              {...{
                className: 'as-placeholder__legalButton',
                [CLIENTSIDE_HOOK_DATA_ATTR]:
                  AD_BANNER_LEGAL_BUTTON_CLIENTSIDE_HOOK,
                type: 'button',
                hidden: true,
                'aria-hidden': true,
              }}
            >
              <span className="u-visuallyhidden">
                <Trans id="general.label.legal.button" />
              </span>
            </button>
          )}
        </div>
      </div>
    )
  }
)

AdBanner.displayName = 'AdBanner'

AdBanner.defaultProps = {
  excludePageSpecificContext: false,
  events: [],
  optional: false,
  isAutoLoaded: false,
  renderBannerOnServerSide: false,
  roundedCorners: true,
}
export default AdBanner
